import { Capacitor, registerPlugin } from "@capacitor/core";
import type { StateStorage } from "./index";

interface StorageNamespace { dbName: string; storeName: string }
interface RecordKey extends StorageNamespace { key: string }
interface NativeRecord { found: boolean; value: string | null }
interface SQLitePlugin {
  read(options: RecordKey): Promise<NativeRecord>;
  write(options: RecordKey & { value: string }): Promise<void>;
  importRecord(options: RecordKey & { value: string }): Promise<NativeRecord>;
  remove(options: RecordKey): Promise<void>;
  clearDatabase(options: { dbName: string }): Promise<void>;
}

const sqlite = registerPlugin<SQLitePlugin>("GGSQLiteStorage");
const pending = new Map<string, Promise<unknown>>();
const failedLoads = new Set<string>();
export const importLegacyItem = Symbol("importLegacyItem");
type MigratableStorage = StateStorage & {
  [importLegacyItem]?: (key: string, value: string) => Promise<string | null>;
};

/** Native Android only. iOS (including Capacitor), Web Clip and H5 stay on IndexedDB.
 * Older installed APK shells without this plugin keep their existing storage path.
 */
export function usesAndroidSQLite() {
  return Capacitor.getPlatform() === "android" && Capacitor.isNativePlatform()
    && Capacitor.isPluginAvailable("GGSQLiteStorage");
}

// Shared across factory instances. A slow legacy read must finish before a later
// save/delete; a rejected operation must not poison the subsequent queue.
function ordered<T>(dbName: string, action: () => Promise<T>): Promise<T> {
  const previous = pending.get(dbName) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(action);
  pending.set(dbName, next);
  const clean = () => { if (pending.get(dbName) === next) pending.delete(dbName); };
  void next.then(clean, clean);
  return next;
}

export function withAndroidSQLite(
  namespace: StorageNamespace,
  browser: StateStorage,
  readLegacy: (key: string) => Promise<string | null>,
): MigratableStorage {
  return {
    getItem(key) {
      if (!usesAndroidSQLite()) return browser.getItem(key);
      return ordered(namespace.dbName, async () => {
        const options = { ...namespace, key };
        const identity = JSON.stringify([namespace.dbName, namespace.storeName, key]);
        try {
          const record = await sqlite.read(options);
          let value = record.value;
          if (!record.found) {
            value = await readLegacy(key);
            // Native transaction imports only if absent and verifies the stored value.
            // Leave IndexedDB/localStorage untouched as the pre-upgrade backup.
            if (value !== null) value = (await sqlite.importRecord({ ...options, value })).value;
          }
          failedLoads.delete(identity);
          return value; // Includes persistent deletion tombstones.
        } catch (error) { failedLoads.add(identity); throw error; }
      });
    },
    setItem(key, value) {
      if (!usesAndroidSQLite()) return browser.setItem(key, value);
      return ordered(namespace.dbName, () => {
        // Some older games catch load failures and render a default state. Do not
        // let their autosave overwrite the unread progress until reading succeeds.
        if (failedLoads.has(JSON.stringify([namespace.dbName, namespace.storeName, key]))) {
          throw new Error("请先重试读取本地存档，避免覆盖尚未恢复的进度。");
        }
        return sqlite.write({ ...namespace, key, value });
      });
    },
    removeItem(key) {
      if (!usesAndroidSQLite()) return browser.removeItem(key);
      return ordered(namespace.dbName, async () => {
        await sqlite.remove({ ...namespace, key });
        failedLoads.delete(JSON.stringify([namespace.dbName, namespace.storeName, key]));
      });
    },
    async [importLegacyItem](key, value) {
      if (!usesAndroidSQLite()) { await browser.setItem(key, value); return value; }
      return ordered(namespace.dbName, async () =>
        (await sqlite.importRecord({ ...namespace, key, value })).value);
    },
  };
}

export async function importIntoStorage(primary: StateStorage, key: string, value: string) {
  const importItem = (primary as MigratableStorage)[importLegacyItem];
  if (importItem) return importItem(key, value);
  await primary.setItem(key, value);
  return value;
}

export function clearAndroidDatabase(dbName: string) {
  return ordered(dbName, async () => {
    await sqlite.clearDatabase({ dbName });
    for (const identity of failedLoads) if (JSON.parse(identity)[0] === dbName) failedLoads.delete(identity);
  });
}
